import { LoadingIndicator, Alert, Button, Skeleton, StatusBadge } from './ui/Primitives';
import { contentPreferenceRevision, subscribeContentPreferences } from '../utils/contentPreferences';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isCallId, redirectToCallSignIn } from '../utils/callLinks';
import axios from 'axios';
import flashMessage from './HelperFunctions';
import { formatAmericanPhoneNumber } from '../utils/phoneNumbers';
import {
  callDisplayStatus,
  callSubject,
  formatCallDuration,
  formatCallTime,
  statusLabel,
} from './CallHistoryItem';
import './CallDetailDrawer.css';
import CallFollowUpPanel from './CallFollowUpPanel';
import TranscriptLanguageView from './TranscriptLanguageView';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('jwt_authorization')}` });

function outcomeTone(status) {
  return status === 'failed' ? 'danger' : /pending|partial|queued|retrying/.test(status || '') ? 'warning' : 'success';
}

const PROVIDER_LABELS = {
  asana: 'Asana',
  hubspot: 'HubSpot',
  salesforce: 'Salesforce',
};

function providerLabel(provider) {
  const providerId = String(provider || '').trim().toLowerCase();
  return PROVIDER_LABELS[providerId]
    || providerId.replace(/[_-]+/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());
}

function messageDeliveryStatusLabel(status) {
  const labels = {
    delivered: 'Sent',
    sent: 'Sent',
    fallback_delivered: 'Sent by fallback',
    fallback_sent: 'Sent by fallback',
    partial: 'Partially sent',
    fallback_partial: 'Partially sent by fallback',
    failed: 'Failed',
    queued: 'Queued',
    retrying: 'Retrying',
    pending: 'Pending',
  };
  return labels[status] || String(status || 'pending').replace(/_/g, ' ');
}

function emailWasSent(status) {
  return status === 'sent' || status === 'delivered';
}

const FALLBACK_LABELS = {
  caller_chose_general: 'The caller agreed to leave a General message instead of a message for the originally requested person.',
  member_not_found: 'The requested person was not found.',
  member_inactive: 'The requested person is inactive.',
  member_email_unavailable: 'The requested person does not have a usable email address.',
  member_delivery_failed: 'Direct email delivery failed.',
  business_recipients_unavailable: 'The configured business recipients were unavailable.',
  business_delivery_failed: 'Delivery to the configured business recipients failed.',
};

const TRANSCRIPT_MARKER_OFFSET_MS = 1000;
const CALLER_MARKER_OFFSET_MS = 2000;

function recordingTimestamp(ms, role) {
  if (ms === null || ms === undefined) return null;
  const offset = role === 'caller' ? CALLER_MARKER_OFFSET_MS : TRANSCRIPT_MARKER_OFFSET_MS;
  return Math.max(Number(ms) - offset, 0);
}

function timestamp(ms, role) {
  const adjustedMs = recordingTimestamp(ms, role);
  if (adjustedMs === null) return null;
  return formatCallDuration(Math.floor(adjustedMs / 1000));
}

export default function CallDetailDrawer({ callId, onClose, onRead, onHandlingChange }) {
  const [call, setCall] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [audioUrl, setAudioUrl] = useState('');
  const [audioLoading, setAudioLoading] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [handlingBusy, setHandlingBusy] = useState(false);
  const audioRef = useRef(null);
  const readRequestRef = useRef(false);
  const handlingMenuRef = useRef(null);
  const textModeRef = useRef(null);
  const loadSequence = useRef(0);
  const contentSwitchRef = useRef(false);
  const [contentSwitchBusy, setContentSwitchBusy] = useState(false);
  const bodyRef = useRef(null);
  const restoreScrollRef = useRef(null);

  useLayoutEffect(() => {
    if (restoreScrollRef.current !== null && bodyRef.current) {
      bodyRef.current.scrollTop = restoreScrollRef.current;
    }
    restoreScrollRef.current = null;
  }, [call]);

  const loadCall = useCallback(async ({ quiet = false, contentMode } = {}) => {
    if (contentSwitchRef.current) return;
    if (!isCallId(callId)) {
      setCall(null);
      setLoadError('unavailable');
      setIsLoading(false);
      return;
    }
    const switchingContent = contentMode !== undefined;
    const previousMode = textModeRef.current;
    if (switchingContent) {
      contentSwitchRef.current = true;
      setContentSwitchBusy(true);
      textModeRef.current = contentMode;
    }
    const sequence = ++loadSequence.current;
    const preferenceRevision = contentPreferenceRevision();
    const token = localStorage.getItem('jwt_authorization');
    const current = () => sequence === loadSequence.current && preferenceRevision === contentPreferenceRevision() && token === localStorage.getItem('jwt_authorization');
    if (!quiet) setIsLoading(true);
    try {
      const baseUrl = `${process.env.REACT_APP_BACKEND_HOST}/v2/calls/${callId}`;
      const response = textModeRef.current === 'original'
        ? await axios.post(`${baseUrl}/original-content`, {}, { headers: authHeaders() })
        : await axios.get(baseUrl, { headers: authHeaders(), params: textModeRef.current === 'redacted' ? { content: 'redacted' } : undefined });
      if (!current()) return;
      const nextCall = response.data.call;
      if (switchingContent) restoreScrollRef.current = bodyRef.current?.scrollTop ?? null;
      setLoadError(nextCall ? '' : 'unavailable');
      setCall(nextCall);
      if (
        nextCall?.messageAssignment?.assignedToYou
        && nextCall.messageAssignment.isRead === false
        && !readRequestRef.current
      ) {
        readRequestRef.current = true;
        axios.put(
          `${process.env.REACT_APP_BACKEND_HOST}/v2/calls/${callId}/read`,
          { read: true },
          { headers: authHeaders() },
        ).then(readResponse => {
          setCall(current => current ? {
            ...current,
            messageAssignment: {
              ...current.messageAssignment,
              isRead: true,
              readAt: readResponse.data.readAt,
            },
          } : current);
          if (onRead) onRead(readResponse.data);
        }).catch(() => { readRequestRef.current = false; });
      }
    } catch (error) {
      if (!current()) return;
      if (switchingContent) {
        textModeRef.current = previousMode;
        flashMessage('error', 'Call text could not be switched. Please try again.');
      }
      setLoadError([403, 404].includes(error.response?.status) ? 'unavailable' : 'failed');
      if ([401, 403, 404].includes(error.response?.status)) {
        setCall(null);
        setAudioUrl('');
      }
      if (error.response?.status === 401) redirectToCallSignIn();
    } finally {
      if (current()) {
        if (!quiet) setIsLoading(false);
        if (switchingContent) {
          contentSwitchRef.current = false;
          setContentSwitchBusy(false);
        }
      }
    }
  }, [callId, onRead]);

  useEffect(() => {
    readRequestRef.current = false;
    contentSwitchRef.current = false;
    setContentSwitchBusy(false);
    restoreScrollRef.current = null;
    textModeRef.current = null;
    setCall(null);
    if (handlingMenuRef.current) handlingMenuRef.current.open = false;
    loadCall();
    const unsubscribe = subscribeContentPreferences(() => {
      loadSequence.current += 1;
      contentSwitchRef.current = false;
      setContentSwitchBusy(false);
      restoreScrollRef.current = null;
      textModeRef.current = null;
      setCall(null);
      loadCall();
    });
    return () => { loadSequence.current += 1; unsubscribe(); };
  }, [loadCall]);

  const toggleContent = () => {
    loadCall({ quiet: true, contentMode: call?.contentRedaction?.visibility === 'redacted' ? 'original' : 'redacted' });
  };

  const callStatus = callDisplayStatus(call);
  const callNeedsRefresh = Boolean(call && (
    ['queued', 'initiated', 'ringing', 'in-progress', 'processing'].includes(callStatus)
    || call.transcriptStatus === 'pending'
    || ['pending', 'starting', 'processing'].includes(call.recordingStatus)
  ));
  const readyRecording = call?.recordings?.find(item => item.status === 'ready');
  const readyRecordingId = readyRecording?.id;
  const hasMessageHandling = Boolean(call?.messageHandling);

  useEffect(() => {
    if (!callNeedsRefresh && !hasMessageHandling) return undefined;
    const timer = window.setInterval(() => loadCall({ quiet: true }), 5000);
    return () => window.clearInterval(timer);
  }, [callNeedsRefresh, hasMessageHandling, loadCall]);

  useEffect(() => {
    if (!readyRecordingId || !call?.canListen) return undefined;
    let active = true;
    setAudioLoading(true);
    axios.get(`${process.env.REACT_APP_BACKEND_HOST}/v2/calls/${call.id}/recordings/${readyRecordingId}/playback`, { headers: authHeaders() })
      .then(response => { if (active) setAudioUrl(response.data.url || ''); })
      .catch(() => flashMessage('error', 'Recording playback could not be loaded'))
      .finally(() => { if (active) setAudioLoading(false); });
    return () => { active = false; };
  }, [call?.canListen, call?.id, readyRecordingId]);

  useEffect(() => {
    const close = event => {
      if (event.key !== 'Escape') return;
      if (handlingMenuRef.current?.open) {
        handlingMenuRef.current.open = false;
        handlingMenuRef.current.querySelector('summary')?.focus();
      } else {
        onClose();
      }
    };
    const closeMenu = event => {
      if (handlingMenuRef.current && !handlingMenuRef.current.contains(event.target)) {
        handlingMenuRef.current.open = false;
      }
    };
    document.addEventListener('keydown', close);
    document.addEventListener('pointerdown', closeMenu);
    return () => {
      document.removeEventListener('keydown', close);
      document.removeEventListener('pointerdown', closeMenu);
    };
  }, [onClose]);

  function changeRate(event) {
    const rate = Number(event.target.value);
    setPlaybackRate(rate);
    if (audioRef.current) audioRef.current.playbackRate = rate;
  }

  function seek(startMs, role) {
    const adjustedMs = recordingTimestamp(startMs, role);
    if (!audioRef.current || adjustedMs === null) return;
    audioRef.current.currentTime = adjustedMs / 1000;
    audioRef.current.play().catch(() => {});
  }

  async function downloadRecording() {
    const recording = call?.recordings?.find(item => item.status === 'ready');
    if (!recording) return;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_HOST}/v2/calls/${call.id}/recordings/${recording.id}/playback`,
        { headers: authHeaders(), params: { download: 1 } },
      );
      window.location.assign(response.data.url);
    } catch (error) {
      flashMessage('error', 'Recording download could not be prepared');
    }
  }

  async function updateHandling(action) {
    if (handlingBusy || !call?.messageHandling) return;
    if (handlingMenuRef.current?.open) {
      handlingMenuRef.current.open = false;
      handlingMenuRef.current.querySelector('summary')?.focus();
    }
    setHandlingBusy(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_HOST}/v2/calls/${callId}/handling`,
        { action, version: call.messageHandling.version },
        { headers: authHeaders() },
      );
      setCall(current => current ? { ...current, messageHandling: response.data.messageHandling } : current);
      if (onHandlingChange) onHandlingChange(response.data);
    } catch (error) {
      flashMessage('error', error.response?.data?.error?.message || 'Message handling could not be updated');
      await loadCall({ quiet: true });
    } finally {
      setHandlingBusy(false);
    }
  }

  const visibleOutcomes = (call?.outcomes || []).filter(outcome => (
    !(outcome.type === 'message_delivery' && ['skipped', 'not_attempted'].includes(outcome.status))
  ));
  const handling = call?.messageHandling;
  const handlingText = !handling ? '' : handling.status === 'handled'
    ? handling.isHandler ? 'Handled by you' : handling.memberName ? `Handled by ${handling.memberName}` : 'Handled'
    : handling.memberId
      ? handling.isHandler ? 'You’re handling this' : `${handling.memberName || 'A team member'} is handling this`
      : 'Unclaimed';
  const canTakeOver = handling?.canClaim && !handling.isHandler && Boolean(handling.memberId);

  return (
    <div className="call-detail-overlay" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
      <aside className="call-detail-drawer" role="dialog" aria-modal="true" aria-label="Call details">
        <header className="call-detail-header">
          <div><span className="say-eyebrow">Call details</span><h2>{call ? callSubject(call) : 'Conversation'}</h2></div>
          <button type="button" onClick={onClose} aria-label="Close call details">×</button>
        </header>
        {isLoading ? <div className="call-detail-loading"><Skeleton height={90} /><Skeleton height={160} /><Skeleton height={240} /></div> : call ? <div className="call-detail-body" ref={bodyRef}>
          <section className="call-detail-player">
            <div className="call-detail-player-heading">
              <div><span>Call recording</span><small>{call.recordingAvailable ? 'Original audio; text redaction does not change recordings' : 'No recording available'}</small></div>
              {audioUrl ? <select className="say-select" aria-label="Playback speed" value={playbackRate} onChange={changeRate}>
                {[0.75, 1, 1.25, 1.5, 2].map(rate => <option value={rate} key={rate}>{rate}×</option>)}
              </select> : null}
            </div>
            {audioLoading ? <div className="call-detail-audio-wait"><LoadingIndicator label="Preparing recording…" /></div> : audioUrl ? <>
              <audio ref={audioRef} controls preload="metadata" src={audioUrl}>Your browser does not support audio playback.</audio>
              <button className="call-detail-download" type="button" onClick={downloadRecording}>Download MP3</button>
            </> : call.recordingAvailable && !call.canListen ? <Alert tone="warning" title="Recording access is restricted">An organization administrator can change recording access.</Alert> : <p className="call-detail-muted">{call.recordingStatus === 'processing' ? 'The recording is still processing.' : ''}</p>}
          </section>

          <section className="call-detail-summary">
            <div className="call-detail-section-heading">
              <h3>What happened</h3>
              {call.messageAssignment ? <StatusBadge status={call.messageAssignment.kind === 'fallback' ? 'warning' : 'neutral'}>{call.messageAssignment.label}</StatusBadge> : null}
            </div>
            <p>{call.summary || 'A summary is not available for this call.'}</p>
            {visibleOutcomes.length ? <div className="call-detail-outcomes">
              {visibleOutcomes.map((outcome, index) => <StatusBadge status={outcomeTone(outcome.status)} key={`${outcome.type}-${index}`}>{outcome.label}{outcome.provider ? ` · ${providerLabel(outcome.provider)}` : ''}</StatusBadge>)}
            </div> : null}
            {handling ? <div className="call-detail-handling" role="group" aria-label="Message handling" aria-busy={handlingBusy}>
              <div className="call-detail-handling-status" role="status">
                <span title={handling.handledAt ? `Handled ${formatCallTime(handling.handledAt)}` : undefined}>{handlingText}</span>
                {handling.memberId && !handling.handlerAvailable && handling.status !== 'handled' ? <small>Handler no longer has access</small> : null}
              </div>
              <div className="call-detail-handling-actions">
                {handling.canClaim && !handling.isHandler && !handling.memberId ? <Button size="sm" disabled={handlingBusy} onClick={() => updateHandling('claim')}>I’ll handle this</Button> : null}
                {handling.canHandle ? <Button size="sm" disabled={handlingBusy} onClick={() => updateHandling('handle')}>Mark handled</Button> : null}
                {handling.canReopen ? <Button size="sm" variant="secondary" disabled={handlingBusy} onClick={() => updateHandling('reopen')}>Reopen</Button> : null}
                {handling.canRelease || canTakeOver ? <details className="call-detail-handling-menu" ref={handlingMenuRef} onBlur={event => {
                  if (!event.currentTarget.contains(event.relatedTarget)) event.currentTarget.open = false;
                }}>
                  <summary aria-label="More handling actions">⋯</summary>
                  <div className="call-detail-handling-popover">
                    {canTakeOver ? <button type="button" disabled={handlingBusy} onClick={() => updateHandling('claim')}>Take over</button> : null}
                    {handling.canRelease ? <button type="button" disabled={handlingBusy} onClick={() => updateHandling('release')}>Release</button> : null}
                  </div>
                </details> : null}
              </div>
            </div> : null}
          </section>

          <CallFollowUpPanel key={call.id} callId={call.id} onChange={onHandlingChange} />

          {call.messageDelivery && !['skipped', 'not_attempted'].includes(call.messageDelivery.status) ? <section className="call-detail-message-delivery">
            <div className="call-detail-section-heading">
              <h3>Message delivery</h3>
              <StatusBadge status={outcomeTone(call.messageDelivery.status)}>{messageDeliveryStatusLabel(call.messageDelivery.status)}</StatusBadge>
            </div>
            <dl>
              <div><dt>Directory lookup</dt><dd>{call.messageDelivery.recipientType === 'member' ? `Matched ${call.messageDelivery.recipientMemberName || call.messageDelivery.requestedRecipientName || 'a specific team member'}` : call.messageDelivery.requestedRecipientName ? 'General message after a recipient lookup.' : 'General business message.'}</dd></div>
              {call.messageDelivery.requestedRecipientName ? <div><dt>Caller originally requested</dt><dd>{call.messageDelivery.requestedRecipientName}</dd></div> : null}
              <div><dt>Delivery route</dt><dd>{String(call.messageDelivery.deliveryStrategy || 'pending').replace(/_/g, ' ')}</dd></div>
              <div><dt>Message email sent to</dt><dd>{(call.messageDelivery.recipients || []).filter(item => !item.notificationOnly && emailWasSent(item.status)).map(item => item.memberName).join(', ') || 'No email accepted yet'}</dd></div>
              {(call.messageDelivery.recipients || []).some(item => item.notificationOnly && emailWasSent(item.status)) ? <div><dt>Administrator notice</dt><dd>A delivery-failure notice was sent without message contents.</dd></div> : null}
            </dl>
            {call.messageDelivery.fallbackReason ? <Alert tone="warning" title="Routing or delivery note">{FALLBACK_LABELS[call.messageDelivery.fallbackReason] || 'See the recorded delivery route.'}</Alert> : null}
          </section> : null}

          <section className="call-detail-transcript">
            <div><h3>Transcript</h3><small>{audioUrl ? 'Select a timestamp to hear that moment.' : 'Conversation transcript'}</small></div>
            {call.contentRedaction?.canReveal ? <button type="button" className="call-detail-content-toggle" onClick={toggleContent} disabled={contentSwitchBusy} aria-busy={contentSwitchBusy}>{call.contentRedaction.visibility === 'redacted' ? 'Show inappropriate content' : 'Hide inappropriate content'}</button> : null}
            {(call.transcript || []).length ? <TranscriptLanguageView key={call.id} call={call} disabled={contentSwitchBusy}>{(turns, language) => turns.map((turn, index) => <article className={`call-turn call-turn-${turn.role}`} key={`${turn.startMs}-${index}`}>
              <button type="button" disabled={!audioUrl || turn.startMs === null} onClick={() => seek(turn.startMs, turn.role)}>{turn.role === 'caller' ? 'Caller' : 'Receptionist'}{timestamp(turn.startMs, turn.role) ? ` · ${timestamp(turn.startMs, turn.role)}` : ''}</button>
              <p lang={language} dir="auto">{turn.content}</p>
            </article>)}</TranscriptLanguageView> : <p className="call-detail-muted">The transcript is still processing.</p>}
          </section>

          <details className="call-detail-metadata">
            <summary>Call information</summary>
            <dl>
              <div><dt>Caller</dt><dd>{formatAmericanPhoneNumber(call.from) || 'Unavailable'}</dd></div>
              <div><dt>Started</dt><dd>{formatCallTime(call.startedAt)}</dd></div>
              <div><dt>Duration</dt><dd>{formatCallDuration(call.durationSeconds)}</dd></div>
              <div><dt>Status</dt><dd>{statusLabel(callDisplayStatus(call))}</dd></div>
              <div><dt>Ended by</dt><dd>{String(call.endedReason || 'Unavailable').replace(/_/g, ' ')}</dd></div>
            </dl>
          </details>
        </div> : <EmptyCall onClose={onClose} error={loadError} onRetry={loadCall} />}
      </aside>
    </div>
  );
}

function EmptyCall({ onClose, error, onRetry }) {
  return <div className="call-detail-empty" role="alert">
    <p>{error === 'unavailable' ? 'This call is unavailable or you don’t have access.' : 'Call details could not be loaded. Please try again.'}</p>
    {error === 'failed' ? <Button onClick={() => onRetry()}>Try again</Button> : null}
    <Button variant="secondary" onClick={onClose}>Close</Button>
  </div>;
}
